import { type History, HistoryEntryTypeEnum, type Task } from "@repo/api";
import {
  arrowCounterclockwiseIcon,
  arrowUpArrowDownIcon,
  bellIcon,
  calendarCancelIcon,
  calendarIcon,
  calendarRescheduleIcon,
  chatIcon,
  clipboardCheckmarkIcon,
  clipboardPencilIcon,
  clockIcon,
  crossCircleIcon,
  documentAlertIcon,
  documentCheckmarkIcon,
  documentFinancialIcon,
  documentIcon,
  documentUploadIcon,
  informationCircleIcon,
  laptopPencilIcon,
  mailIcon,
  noteIcon,
  pencilIcon,
  personIcon,
  phoneIcon,
  shoppingBagAddIcon,
  shoppingBagIcon,
  starIcon,
  startFilledIcon,
} from "@repo/skapa";
import { type TaskConfig, getTaskIcon } from "@repo/ui";

const DEFAULT_ICON = noteIcon;

const historyIcons: Record<History["entryType"], any> = {
  GENERIC: bellIcon,
  // Appointment
  APPOINTMENT_ASSIGNED: personIcon,
  APPOINTMENT_CANCELLED: calendarCancelIcon,
  APPOINTMENT_COMPLETED: calendarIcon,
  APPOINTMENT_CREATED: calendarIcon,
  APPOINTMENT_NO_SHOW: crossCircleIcon,
  APPOINTMENT_PAID: clipboardCheckmarkIcon,
  APPOINTMENT_REASSIGNED: personIcon,
  APPOINTMENT_RESCHEDULED: calendarRescheduleIcon,
  APPOINTMENT_STARTED: clockIcon,
  // Communication
  COMMUNICATION_FAILED: informationCircleIcon,
  COMMUNICATION_SENT: mailIcon,
  // Consent
  CONSENT_UPDATE: personIcon,
  CUSTOMER_FOLLOWUP_CONSENT_OPT_OUT: personIcon,
  // Notes
  CUSTOM_NOTES: noteIcon,
  REMARK_UPDATE: noteIcon,
  CUSTOM_NOTE_EDITED: pencilIcon,
  CUSTOM_NOTE_PINNED: noteIcon,
  CUSTOM_NOTE_UNPINNED: noteIcon,
  // Draft order
  DRAFT_ORDER_CONVERTED_UPDATE: shoppingBagIcon,
  DRAFT_ORDER_DELETED: shoppingBagIcon,
  DRAFT_ORDER_IMPORTED: shoppingBagAddIcon,
  // File
  FILE_UPLOADED: documentUploadIcon,
  FILE_DELETED: documentIcon,
  FILE_SHARED: documentCheckmarkIcon,
  FILE_UNSHARED: documentAlertIcon,
  // Update
  CLASSIFICATION_UPDATE: arrowUpArrowDownIcon,
  HFB_UPDATE: pencilIcon,
  INTEREST_SERVICES_UPDATE: pencilIcon,
  PROJECT_VALUE_UPDATE: documentFinancialIcon,
  TITLE_UPDATE: pencilIcon,
  EXPECTED_PURCHASE_DATE: calendarIcon,
  // Order
  ORDER_DELETED: shoppingBagIcon,
  ORDER_IMPORTED: shoppingBagAddIcon,
  ORDER_UPDATE: shoppingBagIcon,
  // Waiting list order
  WAITING_LIST_RESTOCKED: arrowCounterclockwiseIcon,
  // Planner
  PLANNER_ASSOCIATED: laptopPencilIcon,
  PLANNER_DELETED: laptopPencilIcon,
  // Status
  PROJECT_CREATED: starIcon,
  STATUS_UPDATE_ASSIGNED: personIcon,
  STATUS_UPDATE_CLOSED_LOST: crossCircleIcon,
  STATUS_UPDATE_CLOSED_WON: startFilledIcon,
  STATUS_UPDATE_IN_PROGRESS: pencilIcon,
  // Quotation
  QUOTATION_ASSOCIATED: shoppingBagAddIcon,
  QUOTATION_DELETED: shoppingBagIcon,
  // Task
  TASK_UPDATE: clipboardPencilIcon,
  // B2B
  B2B_ENR_FULFILLMENT_UPDATE: shoppingBagIcon,
  // Spoke with customer
  CUSTOMER_CALLED_IN: phoneIcon,
  CALLED_THE_CUSTOMER: phoneIcon,
  WALK_IN_CUSTOMER: chatIcon,
  CUSTOMER_EMAILED: mailIcon,
  // FE only. TODO: remove
  ORDER: shoppingBagIcon,
  APPOINTMENT: calendarIcon,
  PLANNER: laptopPencilIcon,
  FILE: documentIcon,
  FIELD_UPDATE: pencilIcon,
  CLASSIFICATION: pencilIcon,
  SCHEDULED_CALL: phoneIcon,
  PRE_CALL: phoneIcon,

  [HistoryEntryTypeEnum.ASSIGNED_TO_TEAM]: pencilIcon,
  [HistoryEntryTypeEnum.TASK_CREATED]: pencilIcon,
  [HistoryEntryTypeEnum.TASK_REMOVED]: pencilIcon,
  [HistoryEntryTypeEnum.STATUS_UPDATE_INVALIDATED]: pencilIcon,
  [HistoryEntryTypeEnum.APPOINTMENT_DETACHED]: pencilIcon,
  [HistoryEntryTypeEnum.APPOINTMENT_ATTACHED]: pencilIcon,
  [HistoryEntryTypeEnum.FOLLOWUP_APPOINTMENT_CREATED]: pencilIcon,
  [HistoryEntryTypeEnum.FILE_DOWNLOADED]: pencilIcon,
  [HistoryEntryTypeEnum.PLANNER_CONVERTED]: pencilIcon,
  [HistoryEntryTypeEnum.QUOTATION_CANCELLED]: pencilIcon,
  [HistoryEntryTypeEnum.QUOTATION_CONVERTED]: pencilIcon,
  [HistoryEntryTypeEnum.PLANNER_CONVERTED_TO_QUOTATION]: pencilIcon,
  [HistoryEntryTypeEnum.DRAFT_ORDER_CONVERTED_TO_QUOTATION]: pencilIcon,
  [HistoryEntryTypeEnum.PLANNER_CONVERTED_TO_DRAFT_ORDER]: pencilIcon,
  [HistoryEntryTypeEnum.CUSTOMER_OPT_OUT]: pencilIcon,
  [HistoryEntryTypeEnum.CUSTOMER_PROMOTIONAL_CONSENT_OPT_OUT]: pencilIcon,
  [HistoryEntryTypeEnum.PROJECT_REASSIGNED]: pencilIcon,
  [HistoryEntryTypeEnum.CMP_UPDATE]: pencilIcon,
  [HistoryEntryTypeEnum.ORDER_CANCELLED]: pencilIcon,
  [HistoryEntryTypeEnum.ORDER_COMPLETED]: pencilIcon,
  [HistoryEntryTypeEnum.ORDER_ASSOCIATED]: pencilIcon,
  [HistoryEntryTypeEnum.ORDER_CONVERTED]: pencilIcon,
  [HistoryEntryTypeEnum.DUPLICATE_ORDER_ASSOCIATED]: pencilIcon,
  [HistoryEntryTypeEnum.ESTIMATED_BUDGET_UPDATE]: pencilIcon,
  [HistoryEntryTypeEnum.INTEREST_AREA_UPDATE]: pencilIcon,
  [HistoryEntryTypeEnum.SCHEDULE_CALL]: pencilIcon,
  [HistoryEntryTypeEnum.QUOTATION_EXPIRY_COMMUNICATION_SENT]: pencilIcon,
  [HistoryEntryTypeEnum.STAGE_CHANGE]: pencilIcon,
  [HistoryEntryTypeEnum.INSTALLATION_QUOTATION_CREATED]: pencilIcon,
  [HistoryEntryTypeEnum.INSTALLATION_QUOTATION_UPDATE]: pencilIcon,
  [HistoryEntryTypeEnum.SERVICE_WORK_ORDER_UPDATED]: pencilIcon,
  [HistoryEntryTypeEnum.SERVICE_WORK_ORDER_CREATED]: pencilIcon,
};

export const getHistoryIcon = (
  historyType: History["entryType"],
  parentTaskType?: Task["parentTaskType"],
  subTaskType?: Task["subTaskType"],
  taskConfigs?: TaskConfig[]
) => {
  return parentTaskType
    ? getTaskIcon(parentTaskType, subTaskType, taskConfigs)
    : (historyIcons[historyType] ?? DEFAULT_ICON);
};
